/**
 * NBA candidate types + confidence-decay constants (PROD-572).
 *
 * Implements ADR-001 correction 2 + HC8 no-over-suppression: a clinician
 * override reduces an NBA's confidence and re-buckets its priority but
 * never deletes or permanently suppresses it. Every override path through
 * this module is deterministic and bounded by `MIN_NBA_CONFIDENCE`, so an
 * NBA is always re-surfaceable for context-dependent re-evaluation.
 *
 * Sources of truth:
 *   - vault `06-decisions/clinical/ADR-001-clinical-ai-safety-architecture.md`
 *     Layer 4 + Graduated Trust + "this must never change without legal
 *     review" guidance
 *   - master prompt v3.3 §Architecture Constraint correction list (1-2)
 *     and §HC8 ML/RAG Guardrails point 5 ("override events reduce the
 *     priority of the overridden NBA but never eliminate it")
 *   - SPEC §2.2 layer enforcement table + correction 2
 *   - PATHWAY-PRD §6.3 OV-4 "Override does not over-suppress (HC8)"
 *   - COMPONENT-CONTRACTS §9 NbaCard NC-10
 *
 * HC5: zero em / en dashes anywhere. Section separators use `-` (hyphen),
 * commas, periods, or "and" / "to" instead.
 */
import { z } from 'zod';

/**
 * Confidence floor enforced by every reduction path. Even after repeated
 * overrides, an NBA cannot drop below this value. The floor exists so the
 * NBA remains surfaceable at a lower priority for context-dependent
 * re-evaluation, per HC8 + PATHWAY-PRD OV-4.
 *
 * Value chosen so that `state: 'reduced'` NBAs land in the `low` priority
 * bucket but still appear in the candidate set. Tests assert that no
 * sequence of `applyOverrideToNba` calls can push confidence below this
 * value.
 */
export const MIN_NBA_CONFIDENCE = 0.2;

/**
 * Multiplicative decay factor applied per override event. The next
 * confidence is `max(MIN_NBA_CONFIDENCE, current * CONFIDENCE_DECAY_FACTOR)`.
 * Documented in `docs/learning-loop-confidence-decay.md`.
 *
 * Why 0.6: a single override should produce a visible drop without burying
 * the suggestion. With confidence 0.92, one override yields 0.552 (mid
 * bucket); two overrides yield 0.331 (low bucket); three overrides yield
 * 0.20 (the floor). This matches the OV-4 BDD acceptance "confidence
 * reduced from X to Y" pattern.
 */
export const CONFIDENCE_DECAY_FACTOR = 0.6;

/** Boundary between `medium` and `high` priority buckets. */
export const PRIORITY_HIGH_THRESHOLD = 0.85;

/** Boundary between `low` and `medium` priority buckets. */
export const PRIORITY_MEDIUM_THRESHOLD = 0.65;

/**
 * Priority bucket for surface ordering. Derived deterministically from
 * the candidate's `confidence` via `priorityForConfidence`.
 */
export const nbaPrioritySchema = z.enum(['high', 'medium', 'low']);
export type NbaPriority = z.infer<typeof nbaPrioritySchema>;

/**
 * Lifecycle marker for the NBA candidate. ADR-001 + HC8 mandates that an
 * NBA is never deleted as a result of a clinician override; instead its
 * state transitions through `active -> reduced -> restored` per the
 * PATHWAY-PRD §5.6 NBA candidate state machine.
 *
 * - `active`   - default; no override events have been applied
 * - `reduced`  - one or more overrides have decayed confidence; NBA still
 *                surfaces at the lower priority bucket implied by the
 *                decayed confidence
 * - `restored` - confidence has been explicitly raised back via a
 *                supervised retraining or admin reset; NBA surfaces at the
 *                priority implied by the restored confidence
 *
 * The schema does NOT include a `suppressed` value; HC8 forbids that
 * outcome and the type system enforces it.
 */
export const nbaStateSchema = z.enum(['active', 'reduced', 'restored']);
export type NbaState = z.infer<typeof nbaStateSchema>;

/**
 * Canonical NBA candidate payload that the learning loop operates on. The
 * shape is a subset of the full `Nba` fixture in
 * `src/data/synthetic-patients/types.ts` (which carries citations, CTA
 * verb, and learn signal); this leaner record is what the override
 * pipeline reads + writes.
 */
export const nbaCandidateSchema = z.object({
  id: z.string().min(1),
  pathwayId: z.string().min(1),
  stepId: z.string().min(1),
  tenantId: z.string().min(1),
  /** Display title surfaced on the NBA card. */
  title: z.string().min(1),
  /** Confidence in [0, 1]; `MIN_NBA_CONFIDENCE` floor enforced on writes. */
  confidence: z.number().min(0).max(1),
  /** Priority bucket; derived from confidence via `priorityForConfidence`. */
  priority: nbaPrioritySchema,
  /** Lifecycle marker. NBA never enters a `suppressed` state per HC8. */
  state: nbaStateSchema,
  /**
   * Number of override events that have been applied to this candidate.
   * Used by the learning loop to decide when to enter pattern detection
   * (HC8 "minimum override count plus pattern detection required").
   */
  overrideCount: z.number().int().min(0),
  /** ISO timestamp of the last override event applied. */
  lastOverrideAt: z.string().min(1).nullable(),
});

export type NbaCandidate = z.infer<typeof nbaCandidateSchema>;

/**
 * Map a confidence score to its priority bucket. Pure deterministic
 * function; identical inputs always produce identical outputs.
 */
export function priorityForConfidence(confidence: number): NbaPriority {
  if (confidence >= PRIORITY_HIGH_THRESHOLD) return 'high';
  if (confidence >= PRIORITY_MEDIUM_THRESHOLD) return 'medium';
  return 'low';
}

/**
 * Compute the next confidence given the current value and the decay
 * factor. Floors at `MIN_NBA_CONFIDENCE`. Pure function; safe to call from
 * tests without any side effects.
 *
 * Cited in `docs/learning-loop-confidence-decay.md` and asserted in
 * `tests/adr-001-violations.test.ts`.
 */
export function decayConfidence(current: number): number {
  if (!Number.isFinite(current)) {
    throw new Error('decayConfidence: current must be a finite number');
  }
  if (current < 0 || current > 1) {
    throw new Error('decayConfidence: current must be in [0, 1]');
  }
  const next = current * CONFIDENCE_DECAY_FACTOR;
  return Math.max(MIN_NBA_CONFIDENCE, next);
}
